import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { dateTime } from '../lib/format.js';
import { OUTCOME_LABEL, REASON_LABEL, label } from '../lib/labels.js';
import { ConfirmDialog, EmptyState, ErrorState, Icon, Loading, Panel } from '../components/ui.js';
import type { Tone } from '../components/ui.js';
import { Badge as Pill } from '../components/2b-status.js';
import { SendTarget } from '../components/2b-sendtarget.js';
import type { Me } from '../lib/session.js';
import '../screens-2b.css';

/** Simulace cesty (D-129): scénář → náhled → ostré odeslání v simulovaném čase. */

interface Scenario {
  id: string;
  name: string;
  description: string;
  expectedSends: number;
  days: number;
}

interface Received {
  simAt: string;
  day: number | null;
  code: string;
  name: string;
  channel: 'service' | 'marketing';
  subject: string | null;
  templateKey: string | null;
  templatePublished: boolean | null;
  templateUploaded: boolean | null;
  mergedCodes: string[];
  order: number;
  localAt: string;
  zone: string;
  ruleText: string;
  window: { name: string; fromHour: number; toHour: number };
  expectedAt: string | null;
  expectedLocalAt: string | null;
  validUntil: string | null;
  deltaMinutes: number | null;
  checks: { notBeforeAnchor: boolean; inWindow: boolean; withinValidity: boolean };
  ok: boolean;
}

interface NotReceived {
  code: string;
  name: string;
  channel: string;
  outcome: string;
  reason: string;
  explanation: string;
  day: number | null;
}

interface Blocked {
  code: string;
  name: string;
  channel: 'service' | 'marketing';
  cause: 'global_switch_off' | 'switch_off' | 'brand_switch_off' | 'template_missing' | 'send_mode';
  explanation: string;
  fixable: boolean;
}

interface Readiness {
  ready: boolean;
  blocked: Blocked[];
  notices: string[];
}

interface Preview {
  scenario: Scenario;
  brand: string;
  recipientMasked: string;
  mode: 'dry_run' | 'test_allowlist';
  simStart: string;
  simEnd: string;
  received: Received[];
  notReceived: NotReceived[];
  counts: { sent: number; service: number; marketing: number };
  warnings: string[];
  readiness: Readiness;
}

interface Step {
  kind: 'action' | 'decision';
  simAt: string;
  day: number | null;
  label?: string;
  code?: string;
  name?: string;
  outcome?: string;
  reason?: string;
  subject?: string | null;
  providerMessageId?: string | null;
  sendStatus?: string | null;
  error?: string | null;
  reactions?: Record<string, string | null>;
  reactionState?: 'na' | 'pending' | 'matched' | 'unmatched';
}

interface Run {
  id: string;
  scenario: string;
  scenarioName: string;
  brand: string;
  status: 'queued' | 'running' | 'done' | 'failed' | 'cancelled';
  recipientMasked: string;
  simStart: string;
  simEnd: string;
  simNow: string;
  progress: number;
  sent: number;
  planned: number;
  error: string | null;
  steps: Step[];
  createdAt: string;
}

const STATUS: Record<Run['status'], { tone: Tone; text: string }> = {
  queued: { tone: 'warn', text: 'Čeká na spuštění' },
  running: { tone: 'warn', text: 'Běží' },
  done: { tone: 'ok', text: 'Hotovo' },
  failed: { tone: 'bad', text: 'Zastaveno chybou' },
  cancelled: { tone: 'neu', text: 'Zrušeno' },
};

const REACTION_LABEL: Record<string, string> = {
  delivered: 'doručeno',
  opened: 'otevřeno',
  clicked: 'kliknuto',
  bounced: 'odskočeno',
  unsubscribed: 'odhlášeno',
  complained: 'stížnost',
};

const NOT_PLANNED_LABEL: Record<string, string> = {
  min_lead: 'krátký předstih',
  too_late: 'termín by byl před nákupem',
  after_end: 'po konci simulace',
  needs_date: 'závod nemá potvrzený termín',
  event_missing: 'nenastala událost',
  no_object: 'scénář to neobsahuje',
  not_triggered: 'nevyvolalo se',
};

const CAUSE_LABEL: Record<Blocked['cause'], string> = {
  global_switch_off: 'vypnutý hlavní vypínač',
  switch_off: 'vypnutý vypínač zprávy',
  brand_switch_off: 'vypnuté odesílání značky',
  template_missing: 'chybí publikovaná šablona',
  send_mode: 'režim odesílání nanečisto',
};

const dayText = (d: number | null): string =>
  d === null ? '—' : d === 0 ? 'den závodu' : `${d < 0 ? '−' : '+'}${Math.abs(d)} d`;

const channelText = (c: string): string => (c === 'marketing' ? 'marketing' : 'servis');

function reasonText(n: NotReceived): string {
  if (n.explanation) return n.explanation;
  if (n.reason.startsWith('merged:')) return `sloučeno do zprávy ${n.reason.slice(7)}`;
  return label(REASON_LABEL, n.reason);
}

function ReadinessPanel({
  r,
  busy,
  onPrepare,
}: {
  r: Readiness;
  busy: boolean;
  onPrepare: () => void;
}): JSX.Element | null {
  if (r.ready && r.notices.length === 0) return null;
  const fixable = r.blocked.some((b) => b.fixable);
  return (
    <div
      className={`confirm${r.ready ? '' : ' dngc'}`}
      role={r.ready ? 'status' : 'alert'}
      data-testid="journey-readiness"
    >
      <b>Připravenost cesty</b>
      {r.ready ? null : (
        <>
          <p>
            Tyto zprávy se v simulaci <b>neodešlou</b>, protože zkušební prostředí není připravené:
          </p>
          <ul>
            {r.blocked.map((b) => (
              <li key={`${b.code}-${b.cause}`}>
                <b>{b.code}</b> <span className="note">{b.name}</span>: {CAUSE_LABEL[b.cause]}.{' '}
                {b.explanation}
              </li>
            ))}
          </ul>
        </>
      )}
      {r.notices.map((n) => (
        <p key={n} className="note">
          {n}
        </p>
      ))}
      {fixable ? (
        <div className="row">
          <button className="btn pri" disabled={busy} onClick={onPrepare}>
            Připravit zkušební prostředí pro tuto cestu
          </button>
          <span className="note">
            Zapne vypínače zpráv této cesty (odesílat se bude dál jen na zkušební adresu) a doplní
            ukázkové šablony „text doplnit“. Existující šablony nemění.
          </span>
        </div>
      ) : null}
    </div>
  );
}

function PreviewTables({ p }: { p: Preview }): JSX.Element {
  return (
    <>
      {p.warnings.map((w) => (
        <div key={w} className="confirm" role="alert">
          <span>{w}</span>
        </div>
      ))}
      <h4>Co dostanete a kdy</h4>
      <p className="note">
        Simulace začíná nákupem {dateTime(p.simStart)} a končí {dateTime(p.simEnd)}. Čas je místní
        čas příjemce ({p.received[0]?.zone ?? 'Europe/Prague'}). Celkem {p.counts.sent} e-mailů (
        {p.counts.service}× servis, {p.counts.marketing}× marketing), na {p.recipientMasked}.
      </p>
      {p.received.length === 0 ? (
        <EmptyState title="Žádná zpráva by neodešla">
          Podle náhledu by se v této cestě neodeslala žádná zpráva.
        </EmptyState>
      ) : (
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>Simulované datum a čas</th>
                <th>Den</th>
                <th>Zpráva</th>
                <th>Předmět</th>
                <th>Šablona</th>
                <th>Z čeho čas vyplývá</th>
              </tr>
            </thead>
            <tbody>
              {p.received.map((r) => (
                <tr key={`${r.order}-${r.code}`}>
                  <td>{r.order}</td>
                  <td>{r.localAt}</td>
                  <td>{dayText(r.day)}</td>
                  <td>
                    <b>{r.code}</b> <span className="note">{r.name}</span>
                    <div className="note">{channelText(r.channel)}</div>
                    {r.mergedCodes.length > 0 ? (
                      <div className="note">sloučeno: {r.mergedCodes.join(', ')}</div>
                    ) : null}
                  </td>
                  <td>{r.subject ?? '—'}</td>
                  <td>
                    {r.templatePublished === false ? (
                      <Pill tone="bad">nepublikovaná</Pill>
                    ) : (
                      <Pill tone="ok">publikovaná</Pill>
                    )}
                    {r.channel === 'marketing' ? (
                      r.templateUploaded ? (
                        <Pill tone="ok">nahraná</Pill>
                      ) : (
                        <Pill tone="warn">nahraje se při spuštění</Pill>
                      )
                    ) : null}
                  </td>
                  <td className="note">{r.ruleText}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <h4>Očekáváno vs. skutečně</h4>
      <p className="note">
        Očekávaný čas je termín z pravidla (vztažný bod + posun). Skutečný čas je okamžik v
        simulaci, kdy zpráva odešla: nejdřív po termínu, v čase vhodném pro příjemce a před koncem
        platnosti.
      </p>
      <div className="scroll">
        <table data-testid="expected-vs-actual">
          <thead>
            <tr>
              <th>Zpráva</th>
              <th>Očekáváno (z pravidla)</th>
              <th>Skutečně (simulace)</th>
              <th>Rozdíl</th>
              <th>Vhodný čas</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {p.received.map((r) => (
              <tr key={`${r.order}-${r.code}-cmp`}>
                <td>
                  <b>{r.code}</b>
                </td>
                <td>{r.expectedLocalAt ?? '—'}</td>
                <td>{r.localAt}</td>
                <td>{r.deltaMinutes === null ? '—' : `${r.deltaMinutes} min`}</td>
                <td>
                  {r.window.name === 'immediate'
                    ? 'ihned'
                    : `${r.window.fromHour}–${r.window.toHour} h`}
                </td>
                <td>
                  <Pill tone={r.ok ? 'ok' : 'bad'}>
                    {r.ok
                      ? 'v pořádku'
                      : !r.checks.notBeforeAnchor
                        ? 'před termínem'
                        : !r.checks.inWindow
                          ? 'mimo vhodný čas'
                          : 'po platnosti'}
                  </Pill>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <h4>Co nedostanete a proč</h4>
      <div className="scroll">
        <table>
          <thead>
            <tr>
              <th>Zpráva</th>
              <th>Výsledek</th>
              <th>Důvod</th>
            </tr>
          </thead>
          <tbody>
            {p.notReceived.map((n, i) => (
              <tr key={`${n.code}-${i}`}>
                <td>
                  <b>{n.code}</b> <span className="note">{n.name}</span>
                </td>
                <td>
                  {n.outcome === 'not_planned'
                    ? (NOT_PLANNED_LABEL[n.reason] ?? 'nevyvolalo se')
                    : label(OUTCOME_LABEL, n.outcome)}
                </td>
                <td>{reasonText(n)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function RunView({ id, onGone }: { id: string; onGone: () => void }): JSX.Element {
  const client = useQueryClient();
  const run = useQuery<{ run: Run }>({
    queryKey: ['journey-run', id],
    queryFn: () => api.get(`/api/send-tests/journey/runs/${id}`),
    refetchInterval: (q) => {
      const s = q.state.data?.run.status;
      return s === 'queued' || s === 'running' ? 3000 : 15_000;
    },
  });
  const [error, setError] = useState<string | null>(null);
  const [askDelete, setAskDelete] = useState(false);
  const act = useMutation({
    mutationFn: (kind: 'cancel' | 'resume') =>
      api.post(`/api/send-tests/journey/runs/${id}/${kind}`, {}),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['journey-run', id] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const remove = useMutation({
    mutationFn: () => api.del(`/api/send-tests/journey/runs/${id}`),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['journey-runs'] });
      onGone();
    },
    onError: (e: Error) => setError(e.message),
  });

  if (run.isLoading) return <Loading what="běh simulace" />;
  if (run.isError)
    return (
      <ErrorState
        title="Běh simulace se nepodařilo načíst"
        error={run.error}
        onRetry={() => void run.refetch()}
      />
    );
  const r = run.data?.run;
  if (!r) return <Loading />;
  const active = r.status === 'queued' || r.status === 'running';
  const st = STATUS[r.status];
  const sent = r.steps.filter((s) => s.outcome === 'sent');

  return (
    <div aria-label="Průběh simulace">
      {error ? <ErrorState title="Akce se nepovedla" error={new Error(error)} /> : null}
      <div className="row">
        <Pill tone={st.tone}>{st.text}</Pill>
        <span className="note">
          {r.scenarioName} · {r.recipientMasked} · odesláno {r.sent} z {r.planned} · simulovaný čas{' '}
          {dateTime(r.simNow)}
        </span>
        {active ? (
          <button className="btn" onClick={() => act.mutate('cancel')} disabled={act.isPending}>
            Zrušit
          </button>
        ) : null}
        {r.status === 'failed' || r.status === 'cancelled' ? (
          <button className="btn" onClick={() => act.mutate('resume')} disabled={act.isPending}>
            <Icon name="refresh" size={14} /> Pokračovat
          </button>
        ) : null}
        {!active ? (
          <button type="button" className="btn dng" onClick={() => setAskDelete(true)}>
            Smazat zkušební cestu
          </button>
        ) : null}
      </div>
      <ConfirmDialog
        open={askDelete}
        title="Smazat zkušební cestu?"
        danger
        confirmLabel="Smazat"
        busy={remove.isPending}
        onCancel={() => setAskDelete(false)}
        onConfirm={() => {
          setAskDelete(false);
          remove.mutate();
        }}
      >
        <p>
          Smaže zkušebního zákazníka a všechna data této cesty (zprávy, rozhodnutí, kandidáty,
          objednávky, události). Záznam o odeslaných zkouškách ve Výsledcích zůstane. Zpět to nejde.
        </p>
      </ConfirmDialog>
      <progress
        className="s2b-progress full"
        max={100}
        value={r.progress}
        aria-label="Průběh simulace"
      />
      {r.error ? (
        <div className="confirm dngc" role="alert">
          <span>Důvod zastavení: {r.error}</span>
        </div>
      ) : null}
      <ol className="trace-steps">
        {r.steps.map((s, i) => (
          <li key={i} className={`trace-step ${s.outcome === 'failed' ? 'stopped' : 'ok'}`}>
            <div className="row">
              <b>{dateTime(s.simAt)}</b>
              <span className="note">{dayText(s.day)}</span>
              {s.kind === 'action' ? (
                <span>{s.label}</span>
              ) : (
                <>
                  <b>{s.code}</b>
                  <Pill tone={s.outcome === 'sent' ? 'ok' : s.outcome === 'failed' ? 'bad' : 'neu'}>
                    {s.outcome === 'failed' ? 'Chyba' : label(OUTCOME_LABEL, s.outcome ?? '')}
                  </Pill>
                  <span className="note">{label(REASON_LABEL, s.reason ?? '')}</span>
                </>
              )}
            </div>
            {s.outcome === 'sent' ? (
              <p className="note">
                {s.subject ?? ''} · číslo odeslání {s.providerMessageId ?? '—'}
                {s.reactionState === 'pending' ? ' · čeká na reakci' : ''}
                {s.reactionState === 'unmatched' ? ' · nespárováno' : ''}
                {s.reactions
                  ? Object.entries(s.reactions)
                      .filter(([, at]) => at)
                      .map(([t]) => ` · ${REACTION_LABEL[t] ?? t}`)
                      .join('')
                  : ''}
              </p>
            ) : null}
            {s.error ? <p className="note">{s.error}</p> : null}
          </li>
        ))}
      </ol>
      {sent.length > 0 ? (
        <p className="note">
          Reakce (doručení, otevření, klik) dorazí později se stahováním událostí ze SmartEmailingu
          (každých 15 minut, nebo Úlohy → Spustit teď u „Reakce ze SmartEmailingu“). Zprávy vidíte i
          v záložce Odeslané.
        </p>
      ) : null}
    </div>
  );
}

export function JourneySim({
  me,
  brandDefault = 'TGP',
}: {
  me: Me;
  brandDefault?: string;
}): JSX.Element {
  const client = useQueryClient();
  const [brand] = useState(brandDefault);
  const [scenario, setScenario] = useState('in_time_90');
  const [preview, setPreview] = useState<Preview | null>(null);
  const [runId, setRunId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [askStart, setAskStart] = useState(false);

  const scenarios = useQuery<{ items: Scenario[] }>({
    queryKey: ['journey-scenarios'],
    queryFn: () => api.get('/api/send-tests/journey/scenarios'),
    staleTime: 60_000,
  });
  const runs = useQuery<{ items: Run[] }>({
    queryKey: ['journey-runs'],
    queryFn: () => api.get('/api/send-tests/journey/runs'),
    refetchInterval: 10_000,
  });
  useEffect(() => {
    if (!runId && runs.data?.items[0]) setRunId(runs.data.items[0].id);
  }, [runs.data, runId]);

  const doPreview = useMutation({
    mutationFn: () =>
      api.post<{ preview: Preview }>('/api/send-tests/journey/preview', { scenario, brand }),
    onSuccess: (res) => {
      setError(null);
      setInfo(null);
      setAcceptBlocked(false);
      setPreview(res.preview);
    },
    onError: (e: Error) => setError(e.message),
  });
  const [acceptBlocked, setAcceptBlocked] = useState(false);
  const prepare = useMutation({
    mutationFn: () =>
      api.post<{
        preview: Preview;
        switchesTurnedOn: string[];
        templatesCreated: string[];
      }>('/api/send-tests/journey/prepare', { scenario, brand }),
    onSuccess: (res) => {
      setError(null);
      setPreview(res.preview);
      setInfo(
        `Sandbox připraven: zapnuto ${res.switchesTurnedOn.length} vypínačů, vytvořeno ${res.templatesCreated.length} ukázkových šablon.`,
      );
    },
    onError: (e: Error) => setError(e.message),
  });
  const start = useMutation({
    mutationFn: () =>
      api.post<{ dryRun: boolean; preview: Preview; run: Run | null }>(
        '/api/send-tests/journey/runs',
        { scenario, brand, ...(acceptBlocked ? { acceptBlocked: true } : {}) },
      ),
    onSuccess: (res) => {
      setError(null);
      setPreview(res.preview);
      if (res.dryRun) {
        setInfo('Nanečisto: zobrazen jen náhled, nic se neodeslalo ani nezaložilo.');
        return;
      }
      setInfo(null);
      setRunId(res.run?.id ?? null);
      void client.invalidateQueries({ queryKey: ['journey-runs'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const chosen = scenarios.data?.items.find((s) => s.id === scenario);
  const dry = preview?.mode === 'dry_run';
  const blocked = (preview?.readiness.blocked.length ?? 0) > 0;

  return (
    <Panel
      title="Simulace cesty"
      icon="route"
      subtitle="Projde celou cestu jednoho zkušebního zákazníka od nákupu až po zprávy po závodě v simulovaném čase a pošle všechny e-maily, které by katalog odeslal, na zkušební adresu."
    >
      <SendTarget
        me={me}
        what={
          preview
            ? `Zprávy z této cesty půjdou jen na ${preview.recipientMasked}.`
            : 'Nejdřív si zobrazte náhled, ten nic neodesílá. Teprve potom půjde cestu spustit.'
        }
      />
      <div className="note">
        <p>
          Čas je zkrácený: simulace „přeskakuje“ dny, ale e-maily opravdu odcházejí v pauzách po
          několika sekundách, takže vše dorazí během pár minut. V předmětu je simulovaný den vůči
          závodu, například <code>[TEST · −60 d]</code> (60 dní před závodem). Reakce (doručení,
          otevření, klik) přicházejí později se stahováním událostí (každých 15 minut, nebo Úlohy →
          Spustit teď).
        </p>
      </div>
      {error ? <ErrorState title="Simulace se nepovedla" error={new Error(error)} /> : null}
      {info ? (
        <div className="confirm" role="status">
          <span>{info}</span>
        </div>
      ) : null}

      {scenarios.isLoading ? <Loading what="scénáře" /> : null}
      <div className="row s2b-scenarios">
        {(scenarios.data?.items ?? []).map((s) => (
          <button
            key={s.id}
            className={`btn s2b-scenario${s.id === scenario ? ' pri' : ''}`}
            aria-pressed={s.id === scenario}
            onClick={() => {
              setScenario(s.id);
              setPreview(null);
            }}
          >
            <b>{s.name}</b>
            <div className="note">{s.description}</div>
            <div className="note">
              Očekáváno {s.expectedSends} e-mailů za {s.days} simulovaných dní.
            </div>
          </button>
        ))}
      </div>
      <div className="row s2b-gap">
        <button
          type="button"
          className="btn"
          disabled={!chosen || doPreview.isPending}
          onClick={() => doPreview.mutate()}
        >
          <Icon name="flask" size={14} /> Náhled (nic se neodešle)
        </button>
        {preview ? (
          <button
            type="button"
            className="btn pri"
            disabled={
              preview.counts.sent === 0 || (blocked && !acceptBlocked && !dry) || start.isPending
            }
            onClick={() => setAskStart(true)}
          >
            {dry ? 'Spustit nanečisto (jen náhled)' : 'Odeslat všechny e-maily z této cesty'}
          </button>
        ) : null}
        {preview && blocked && !acceptBlocked && !dry ? (
          <span className="note">Některé zprávy se nepošlou (viz Připravenost cesty).</span>
        ) : null}
        {preview && blocked && !dry ? (
          <label className="row">
            <input
              type="checkbox"
              checked={acceptBlocked}
              onChange={(e) => setAcceptBlocked(e.target.checked)}
            />
            <span>Odeslat i přes chybějící šablony/vypínače — tyto zprávy se nepošlou</span>
          </label>
        ) : null}
        {doPreview.isPending ? <span className="note">Počítám náhled…</span> : null}
      </div>

      {preview ? (
        <ReadinessPanel
          r={preview.readiness}
          busy={prepare.isPending}
          onPrepare={() => prepare.mutate()}
        />
      ) : null}
      {preview ? <PreviewTables p={preview} /> : null}

      <h4>Běhy simulace</h4>
      {runs.isLoading ? (
        <Loading what="běhy" />
      ) : (runs.data?.items ?? []).length === 0 ? (
        <EmptyState title="Zatím neběžela žádná simulace">
          Vyberte scénář, zobrazte náhled a cestu spusťte.
        </EmptyState>
      ) : (
        <div className="row s2b-scenarios">
          {(runs.data?.items ?? []).map((r) => (
            <button
              key={r.id}
              className={`btn${r.id === runId ? ' pri' : ''}`}
              onClick={() => setRunId(r.id)}
            >
              {r.scenarioName} · {dateTime(r.createdAt)} · {STATUS[r.status].text}
            </button>
          ))}
        </div>
      )}
      {runId ? <RunView key={runId} id={runId} onGone={() => setRunId(null)} /> : null}

      <ConfirmDialog
        open={askStart && preview !== null}
        title={dry ? 'Spustit cestu nanečisto?' : 'Odeslat e-maily z této cesty?'}
        confirmLabel={dry ? 'Spustit nanečisto' : 'Odeslat'}
        busy={start.isPending}
        onCancel={() => setAskStart(false)}
        onConfirm={() => {
          setAskStart(false);
          start.mutate();
        }}
      >
        <p>
          {dry
            ? 'Prostředí je nastavené nanečisto: nic se neodešle ani nezaloží, zobrazí se jen náhled.'
            : `Pošle se ${preview?.counts.sent ?? 0} e-mailů na ${preview?.recipientMasked ?? ''} (jen zkušební adresa), popořadě s pauzou mezi odesláními. První selhání běh zastaví. Zákazníkům nic nepůjde.`}
        </p>
        <SendTarget me={me} compact />
      </ConfirmDialog>
    </Panel>
  );
}
